import React from 'react';
import { getLabel } from '../../../data/labels';
import { getConfiguration } from '../../../data/utils/configuration';
import { oddToStringCheckbet } from '../../../data/utils/oddToStringCheckbet';
import EventInfo from './EventInfo';
import { WidgetContext, useWidgetContext } from '../../widgets/WidgetContext';
import ScoreAndTime from './ScoreAndTime';
import { xs_isMobile } from '../../..';
import LiveTrackerToggle from './LiveTrackerToggle';
import LiveTracker from './LiveTracker';
import { calculateBetBuilderOdd } from '../../utils/calculateBetBuilderOdd';
import { getEnv } from '../../env';

const getEsitoClass = function (betStatus, winning, winningDrawCode) {
    if (betStatus === 'voided') return 'esito-vd';
    if (!['winning', 'losing'].includes(betStatus) || !winningDrawCode) return 'esito-nd';
    return winning ? 'esito-v' : 'esito-p';
};

function ScEsito({ betStatus, winning, winningDrawCode }) {
    if (betStatus === 'voided')
        return <span className="sc__esito sc__esito--voided">—</span>;
    if (!['winning', 'losing'].includes(betStatus) || !winningDrawCode)
        return <span className="sc__esito sc__esito--nd">•</span>;
    return winning
        ? <span className="sc__esito sc__esito--win">✓</span>
        : <span className="sc__esito sc__esito--lose">✗</span>;
}

function getUrl(event) {
    const conf = getConfiguration();
    let url = null;
    try {
        const routes = window.appConfig.ROUTES;
        const prematchPath = conf.xspu?.pm;
        const livePath = conf.xspu?.lv;
        if (event.state === 'in_progress') {
            url = xs_isMobile
                ? `https://${conf.url}${livePath || routes.live.live}`
                : `https://${conf.url}${livePath || routes.live['live-suite']}`;
        } else {
            url = xs_isMobile
                ? `https://${conf.url}${prematchPath || routes.prematch.prematch}/_${event.sportRef}_${event.categoryRef}_${event.tournamentRef}`
                : `https://${conf.url}${prematchPath || routes.prematch['top-match']}/_${event.sportRef}_${event.categoryRef}_${event.tournamentRef}`;
        }
    } catch (err) {
        console.error(err);
    }
    return url;
}

function getActivePeriodKey(partialScores) {
    if (!partialScores?.length) return null;
    let active = null;
    for (const ps of partialScores) {
        const [key, score] = ps.split('#');
        if (score?.trim().length > 0) active = key;
    }
    return active; // es '1T', '1S', 'G', 'Q1', ecc...
}

function EventsList(props) {
    const conf = getConfiguration();
    const [widgetContext] = useWidgetContext('#br_widget');
    const isV3 = conf.mbt === 'V3';
    const sc = conf.ebv2r;
    const isSnapshot = props.isSnapshot;

    return (
        <WidgetContext.Provider value={widgetContext}>
            {!sc && (
                <div className="grey-box bold | mb_6_1">
                    <div className="mb_6_1_1">
                        <div className="d-flex align-items-center justify-content-center text-medium uppercase desc-event-checkbet | mb_6_1_1_1">
                            Evento
                        </div>
                    </div>
                    <div className="mb_6_1_2">
                        <div className="inner-grid | mb_6_1_2_1">
                            <span className="text-medium | mb_6_1_2_1_1">{getLabel('mybets.segno', 'Segno')}</span>
                            {!props.isBetBuilder && <span className="text-medium | mb_6_1_2_1_2">{getLabel('mybets.quota')}</span>}
                            <span className="text-medium | mb_6_1_2_1_3">{getLabel('mybets.esito', 'Esito')}</span>
                        </div>
                        <div className="mb_6_1_2_1"></div>
                    </div>
                </div>
            )}

            {props.events.map((event, i) => {
                const showBetBuilderOdd = (props.isBetBuilder || event.betBuilderReducedOdd) && conf.bbc.type === 'ITALIA' && event.bets.length > 0;
                const hasScore = event.score?.length > 0;

                return (
                    <React.Fragment key={`${event.id}_${i}`}>
                        <EventItem event={event} isCheckbet={props.isCheckbet}>
                            <div className={`d-flex flex-column margine-su-giu | mb_10_1${sc ? ' sc__event' : ''}`}>
                                <EventInfo isPsv={props.isPsv} event={event} isSystem={props.isSystem} />
                                <div className="mb_10_2">
                                    <div className={`d-flex align-items-center text-medium bold desc-event-checkbet | mb_10_2_1${sc ? ' sc__event-title' : ''}`}>
                                        {event.desc}
                                        {!sc && hasScore &&
                                            <span className="mb_10_2_1_1">{event.score.replace(':', ' - ')}</span>
                                        }
                                        {props.isSystem && !event.var &&
                                            <span className="ml-auto mr-2 bold | mb_10_2_1_2">F</span>
                                        }
                                    </div>
                                    {sc && (
                                        <div className="sc__score-row">
                                            {event.state === 'in_progress' ? (
                                                <>
                                                    <span className="sc__live-badge">LIVE</span>
                                                    {(event.timing > 0 || getActivePeriodKey(event.partialScores)) && (
                                                        <span className="sc__live-timing">
                                                            {event.timing > 0 && `${event.timing}MIN`}
                                                            {getActivePeriodKey(event.partialScores) && (
                                                                `${event.timing > 0 ? ', ' : ''}${getActivePeriodKey(event.partialScores)}`
                                                            )}
                                                        </span>
                                                    )}
                                                </>
                                            ) : (
                                                hasScore && <span className="sc__score-badge">{event.score.replace(':', ' - ')}</span>
                                            )}
                                            <LiveTrackerToggle event={event} isSnapshot={isSnapshot} />
                                        </div>
                                    )}

                                    {!sc && <ScoreAndTime {...event} />}
                                    {!sc && conf.fomb && !isV3 && <LiveTrackerToggle event={event} isSnapshot={isSnapshot} />}
                                </div>
                                <LiveTracker event={event} />
                                {event.bets.map((bet, j) => {
                                	const showOdd = (!props.isBetBuilder || (props.isBetBuilder && conf.bbc.sno)) && !event.betBuilderReducedOdd;
                                    const esitoClass = getEsitoClass(bet.state, bet.isWinning, bet.winningDrawCode);

                                    if (sc) {
                                        return (
                                            <div key={`${bet.id}_${bet.drawCode}_${j}`} className="sc__bet-row | mb_10_3">
                                                <span className="sc__bet-row__desc">
                                                    {bet.drawDesc ? `${bet.desc}: ` : bet.desc}
                                                </span>
                                                {bet.drawDesc && (
                                                    <span className="sc__bet-row__draw">
                                                        {showOdd
                                                            ? `${bet.drawDesc} (${oddToStringCheckbet(bet.odd)})`
                                                            : bet.drawDesc
                                                        }
                                                    </span>
                                                )}
                                                <ScEsito
                                                    betStatus={bet.state}
                                                    winning={bet.isWinning}
                                                    winningDrawCode={bet.winningDrawCode}
                                                />
                                            </div>
                                        );
                                    }

                                    return (
                                        <div key={`${bet.id}_${bet.drawCode}_${j}`} className="grey-box | mb_10_3">
                                            <div className="mb_10_3_1">
                                                <div className="flex | mb_10_3_1_1">
                                                    {conf.fomb && isV3 && <LiveTrackerToggle event={event} isSnapshot={isSnapshot} />}
                                                    <span className="text-medium | mb_10_3_1_1_1">{bet.desc}</span>
                                                </div>
                                            </div>
                                            <div className="mb_10_3_2">
                                                <div className="inner-grid | mb_10_3_2_1">
                                                    <span className="text-medium bold | mb_10_3_2_1_1">{bet.drawDesc}</span>
                                                    {showOdd
                                                        ? <div className="d-flex | mb_10_3_2_1_2">
                                                            <span className="text-medium bold | mb_10_3_2_1_2_1">{oddToStringCheckbet(bet.odd)}</span>
                                                        </div>
                                                        : <div className="mb_10_3_2_1_3"></div>
                                                    }
                                                    <span className={`esito | mb_10_3_2_1_3 ${esitoClass}`}></span>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}

                                {showBetBuilderOdd && (
                                    <div className="grey-box | mb_10_3">
                                        <div className="mb_10_3_1">
                                            <span className="text-medium | mb_10_3_1_1_1">
                                                {getLabel('betbuilder.title')} - {getLabel('mybets.quotatotale')}
                                            </span>
                                        </div>
                                        <div className="mb_10_3_2">
                                            <div className="d-flex | mb_10_3_2_1_2">
                                                <span className="text-medium bold | mb_10_3_2_1_2_1">
                                                    {oddToStringCheckbet(event.betBuilderReducedOdd || calculateBetBuilderOdd(event.bets))}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                )}

                            </div>
                        </EventItem>
                    </React.Fragment>
                );
            })}
        </WidgetContext.Provider>
    );
}

function EventItem(props) {
    const isStarcasino = getEnv('REACT_APP_FRONTEND_VERSION') === 'starcasino';
    return (
        <>
            {isStarcasino || window.appConfig?.XSHOP_TERMINAL || props.isCheckbet
                ? <div className="event d-flex flex-column | mb_10">{props.children}</div>
                : <a href={getUrl(props.event)} target="_blank" rel="noreferrer" className="event d-flex flex-column | mb_10">{props.children}</a>
            }
        </>
    );
}

export default EventsList;